import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  DollarSign,
  Receipt,
  MessageSquare,
  BarChart3,
  LogOut,
  X,
  ChevronRight,
  CreditCard,
  Ticket,
  Bell,
  CalendarPlus,
  Stethoscope,
  List,
  Clock,
  CheckCircle,
  Play,
  UserX,
  Activity,
} from "lucide-react";
import ManualBookingDialog from "../consultations/ManualBookingDialog";
import BrandMark from "../common/BrandMark";
import { useAuthContext } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const menuItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, iconColor: "#3b82f6", bgColor: "hsl(var(--tint-blue))" },
  {
    path: "/consultations", label: "Consultations", icon: MessageSquare, iconColor: "#f59e0b", bgColor: "hsl(var(--tint-amber))",
    badge: "24",
    submenu: [
      { path: "/consultations/all", label: "All Consultations", icon: List },
      { path: "/consultations/active", label: "Active", icon: Play },
      { path: "/consultations/completed", label: "Completed", icon: CheckCircle },
      { path: null, label: "Create Booking", isAction: true, icon: CalendarPlus },
    ],
  },
  {
    path: "/doctors", label: "Doctors", icon: Stethoscope, iconColor: "#0ea5e9", bgColor: "hsl(var(--tint-blue))",
    badge: "12",
    submenu: [
      { path: "/doctors/all", label: "All Doctors", icon: List },
      { path: "/doctors/pending", label: "Pending Verification", icon: Clock },
      { path: "/doctors/verified", label: "Verified", icon: CheckCircle },
    ],
  },
  {
    path: "/patients", label: "Patients", icon: Users, iconColor: "#6366f1", bgColor: "hsl(var(--tint-blue))",
    badge: "1.2k",
    submenu: [
      { path: "/patients/all", label: "All Patients", icon: List },
      { path: "/patients/verified", label: "Healthy (80+)", icon: Activity },
      { path: "/patients/pending", label: "Needs Attention", icon: UserX },
    ],
  },
  {
    path: "/finance", label: "Transactions", icon: CreditCard, iconColor: "#8b5cf6", bgColor: "hsl(var(--tint-violet))",
    submenu: [
      { path: "/transactions", label: "Transactions", icon: Receipt },
      { path: "/withdrawals", label: "Withdrawals", icon: DollarSign },
    ],
  },
  { path: "/coupons", label: "Coupons Manager", icon: Ticket, iconColor: "#ec4899", bgColor: "#fce7f3" },
  { path: "/notifications", label: "Notifications", icon: Bell, iconColor: "#06b6d4", bgColor: "hsl(var(--tint-blue))" },
  { path: "/reports", label: "Reports", icon: BarChart3, iconColor: "#f97316", bgColor: "#ffedd5" },
];

const GROUPS = {
  "/dashboard": "Overview",
  "/doctors": "People",
  "/patients": null,
  "/consultations": "Care",
  "/finance": "Money",
  "/coupons": null,
  "/notifications": "Engage",
  "/reports": null,
};

const SidebarShadcn = ({ isMobile, isOpen, onClose }) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({ "/consultations": true, "/doctors": true });
  const [showManualBookingDialog, setShowManualBookingDialog] = useState(false);

  const toggleExpanded = (path) =>
    setExpandedItems((prev) => ({ ...prev, [path]: !prev[path] }));

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const isSubmenuActive = (submenu) =>
    submenu.some((item) => location.pathname === item.path);

  const { logout, user } = useAuthContext();

  const sidebarContent = (
    <div className="flex h-full flex-col overflow-y-auto border-r bg-gradient-to-b from-card to-card/90">
      {/* Brand */}
      <div className="flex h-16 shrink-0 items-center justify-between px-5">
        <div className="flex items-center gap-3">
          <BrandMark size={34} />
          <div className="leading-tight">
            <div className="text-[15px] font-semibold text-foreground">Soocher</div>
            <div className="text-[11px] font-medium text-muted-foreground">Admin console</div>
          </div>
        </div>
        {isMobile && (
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 pb-3 pt-2">
        <div className="flex flex-col gap-0.5">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedItems[item.path];
            const isItemActive = isActive(item.path) || (hasSubmenu && isSubmenuActive(item.submenu));

            return (
              <div key={item.path}>
                {GROUPS[item.path] && (
                  <div className="mb-1 mt-4 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 first:mt-0">
                    {GROUPS[item.path]}
                  </div>
                )}
                {hasSubmenu ? (
                  <div
                    className={cn("nav-item", isItemActive && "active")}
                    onClick={() => toggleExpanded(item.path)}
                  >
                    <div className="nav-icon-wrap">
                      <Icon size={16} />
                    </div>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.18 }}>
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </motion.div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className={cn("nav-item", isItemActive && "active")}
                  >
                    <div className="nav-icon-wrap">
                      <Icon size={16} />
                    </div>
                    <span style={{ flex: 1 }}>{item.label}</span>
                  </Link>
                )}

                <AnimatePresence>
                  {hasSubmenu && isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="ml-[22px] mt-0.5 flex flex-col gap-0.5 border-l pl-3">
                        {item.submenu.map((subItem) => {
                          if (subItem.isAction) {
                            const ActionIcon = subItem.icon || CalendarPlus;
                            return (
                              <button
                                key={subItem.label}
                                onClick={() => { setShowManualBookingDialog(true); if (isMobile) onClose(); }}
                                className="mt-1 flex w-full items-center gap-2 rounded-lg bg-accent px-2.5 py-2 text-left text-[13px] font-semibold text-accent-foreground hover:bg-accent/70"
                              >
                                <ActionIcon size={14} />
                                {subItem.label}
                              </button>
                            );
                          }
                          const SubIcon = subItem.icon;
                          const subActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={onClose}
                              className={cn(
                                "flex items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] no-underline",
                                subActive
                                  ? "bg-accent font-semibold text-accent-foreground"
                                  : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                              )}
                            >
                              {SubIcon && <SubIcon size={14} className="shrink-0" />}
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </nav>

      {/* Footer */}
      <div className="border-t p-3">
        <div className="flex items-center gap-3 rounded-xl bg-gradient-to-br from-accent/70 to-transparent p-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-indigo-500 text-sm font-semibold text-white shadow-sm">
            {user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[13px] font-semibold text-foreground">
              {user?.displayName || user?.email?.split("@")[0] || "Admin"}
            </p>
            <p className="truncate text-[11px] text-muted-foreground">Administrator</p>
          </div>
          <button
            onClick={logout}
            aria-label="Sign out"
            title="Sign out"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-40"
                style={{ background: "rgba(15,23,42,0.4)" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
              />
              <motion.div
                className="fixed left-0 top-0 h-full z-50"
                style={{ width: 260 }}
                initial={{ x: -260 }}
                animate={{ x: 0 }}
                exit={{ x: -260 }}
                transition={{ type: "spring", damping: 28, stiffness: 220 }}
              >
                {sidebarContent}
              </motion.div>
            </>
          )}
        </AnimatePresence>
        <ManualBookingDialog
          open={showManualBookingDialog}
          onOpenChange={setShowManualBookingDialog}
          onSuccess={() => console.log("Manual booking created successfully")}
        />
      </>
    );
  }

  return (
    <>
      <div className="hidden md:block" style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: 260, zIndex: 40 }}>
        {sidebarContent}
      </div>
      {!isMobile && (
        <ManualBookingDialog
          open={showManualBookingDialog}
          onOpenChange={setShowManualBookingDialog}
          onSuccess={() => console.log("Manual booking created successfully")}
        />
      )}
    </>
  );
};

export default SidebarShadcn;
